import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import { aggregatedMapData } from "./maps";
import { countries } from "@/constants/countries";

type TableData = {
  iso: string;
  label: string;
  value: number;
};

type TableProps = {
  mapData: aggregatedMapData[];
};

export const MapsTable = ({ mapData }: TableProps) => {
  const { t } = useTranslation("boClients");
  const columns = useMemo<MRT_ColumnDef<TableData>[]>(
    () => [
      {
        accessorKey: "label",
        header: t("profileStatisticsMapsTable.columns.location"),
        minSize: 50,
        Cell: ({ row }) => {
          const { iso, label } = row.original;
          return (
            <div className="flex items-center gap-2 truncate">
              {iso && (
                <img
                  src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${iso.toUpperCase()}.svg`}
                  className="mt-0.5 h-4 w-4 shrink-0"
                  alt=""
                />
              )}
              <span className="truncate">{label || "-"}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "value",
        enableColumnOrdering: false,
        enableColumnFilter: false,
        header: t("profileStatisticsMapsTable.columns.expeditions"),
        minSize: 50,
      },
    ],
    [t],
  );

  const countryData: TableData[] = useMemo(() => {
    const countryDict: { [key: string]: TableData } = {};
    mapData.forEach((item) => {
      if (!countryDict[item.iso]) {
        const countryName = countries.find((c) => c.countryCode === item.iso)?.label_fr;
        countryDict[item.iso] = {
          iso: item.iso,
          label: countryName ?? item.label,
          value: 0,
        };
      }
      countryDict[item.iso].value += item.value;
    });
    return Object.values(countryDict).sort((a, b) => b.value - a.value);
  }, [mapData]);

  const table = useMaterialReactTable({
    columns,
    data: countryData,
    enableExpandAll: false, //hide expand all double arrow in column header
    enableExpanding: false,
    layoutMode: "grid",
    enableKeyboardShortcuts: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableSorting: false,
    localization: {
      rowsPerPage: t("profileStatisticsMapsTable.rowsPerPage"),
    },
    muiTableBodyProps: {
      sx: {
        "& .MuiTableCell-root:first-of-type": {
          width: "120px",
          minWidth: "120px",
        },
        "& .MuiTableCell-root:not(:first-of-type)": {
          width: "120px",
          minWidth: "120px",
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        "& .MuiTableCell-root:first-of-type": {
          width: "120px",
          minWidth: "120px",
        },
        "& .MuiTableCell-root:not(:first-of-type)": {
          width: "120px",
          minWidth: "120px",
        },
      },
    },
  });

  return <MaterialReactTable table={table} />;
};
